/* ==========================================================================
   luxury-product.js  →  place in  /assets/luxury-product.js
   Original vanilla-JS implementation for the Luxury Product section
   (belliza-1-5-2-theme-source). No external libraries.

   Defines custom elements:
     <luxury-product-gallery>   thumbnails, zoom, mobile swipe, video
     <luxury-variant-picker>    client-side variant matching + DOM updates
     <luxury-quantity-selector> +/- stepper
     <luxury-buy-buttons>       Cart AJAX add-to-cart with states
     <luxury-accordion>         one-open-at-a-time accordion
     <luxury-sticky-panel>      sticky offset + header-height awareness

   Plus: scroll-reveal, size-guide modal, mobile sticky add-to-cart bar.
   ========================================================================== */

(function () {
  'use strict';

  function getSectionJSON(root, attrName) {
    var sectionId = root.closest('[data-section-id]')
      ? root.closest('[data-section-id]').getAttribute('data-section-id')
      : root.getAttribute('data-section-id');
    var script = root.closest('section')
      ? root.closest('section').querySelector('script[' + attrName + '="' + sectionId + '"]')
      : document.querySelector('script[' + attrName + '="' + sectionId + '"]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Product Gallery                                                     */
  /* ------------------------------------------------------------------ */
  class LuxuryProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-luxury-track]');
      this.thumbsContainer = this.querySelector('.luxury-gallery__thumbs');
      this.slides = Array.from(this.querySelectorAll('[data-luxury-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-luxury-thumb]'));
      this.dots = Array.from(this.querySelectorAll('.luxury-gallery__dot'));
      this.prevBtn = this.querySelector('[data-luxury-prev]');
      this.nextBtn = this.querySelector('[data-luxury-next]');
      this.enableZoom = this.getAttribute('data-enable-zoom') === 'true';
      this.activeIndex = 0;

      this.thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function () {
          this.goTo(index);
        }.bind(this));
      }, this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.prev.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.next.bind(this));

      this.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') this.prev();
        if (event.key === 'ArrowRight') this.next();
      }.bind(this));

      if (this.track) {
        this.track.addEventListener('scroll', this.onTrackScroll.bind(this), { passive: true });
      }

      if (this.enableZoom) {
        this.querySelectorAll('[data-luxury-zoom-wrap]').forEach(function (wrap) {
          wrap.addEventListener('mousemove', this.onZoomMove.bind(this));
          wrap.addEventListener('mouseleave', this.onZoomLeave.bind(this));
        }, this);
      }
    }

    onZoomMove(event) {
      var wrap = event.currentTarget;
      var rect = wrap.getBoundingClientRect();
      var x = ((event.clientX - rect.left) / rect.width) * 100;
      var y = ((event.clientY - rect.top) / rect.height) * 100;
      wrap.style.setProperty('--zoom-x', x + '%');
      wrap.style.setProperty('--zoom-y', y + '%');
      wrap.setAttribute('data-zoom-active', 'true');
    }

    onZoomLeave(event) {
      event.currentTarget.removeAttribute('data-zoom-active');
    }

    onTrackScroll() {
      if (window.innerWidth >= 750 || !this.track) return;
      var slideWidth = this.track.clientWidth;
      if (!slideWidth) return;
      var index = Math.round(this.track.scrollLeft / slideWidth);
      this.setActive(index, { scroll: false });
    }

    goTo(index) {
      this.setActive(index, { scroll: true });
    }

    prev() {
      this.setActive((this.activeIndex - 1 + this.slides.length) % this.slides.length, { scroll: true });
    }

    next() {
      this.setActive((this.activeIndex + 1) % this.slides.length, { scroll: true });
    }

    setActive(index, options) {
      options = options || {};
      if (!this.slides[index]) return;
      this.activeIndex = index;

      this.slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      this.thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      this.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });

      if (options.scroll && this.track && window.innerWidth < 750) {
        this.track.scrollTo({ left: this.slides[index].offsetWidth * index, behavior: 'smooth' });
      }

      this.centerActiveThumb(index);
    }

    /* Keeps the active thumbnail centered in its scroll container — used on
       mobile where thumbnails are a horizontal swipeable carousel, so the
       active one is always centered whether it changed via a thumb tap or
       via swiping the main image. */
    centerActiveThumb(index) {
      var thumb = this.thumbs[index];
      if (!thumb || !this.thumbsContainer) return;
      var containerRect = this.thumbsContainer.getBoundingClientRect();
      var thumbRect = thumb.getBoundingClientRect();
      var offset = (thumbRect.left + thumbRect.width / 2) - (containerRect.left + containerRect.width / 2);
      if (Math.abs(offset) > 1) {
        this.thumbsContainer.scrollBy({ left: offset, behavior: 'smooth' });
      }
    }

    /* Called by LuxuryVariantPicker when a variant with featured_media is selected */
    setActiveByMediaId(mediaId) {
      var index = this.slides.findIndex(function (slide) {
        return slide.getAttribute('data-media-id') === String(mediaId);
      });
      if (index > -1) this.setActive(index, { scroll: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Variant Picker                                                      */
  /* ------------------------------------------------------------------ */
  class LuxuryVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.section = this.closest('section');
      this.variants = getSectionJSON(this, 'data-luxury-product-json') || [];
      this.strings = getSectionJSON(this, 'data-luxury-strings') || {};

      this.inputs = Array.from(this.querySelectorAll('.luxury-variants__radio'));
      this.inputs.forEach(function (input) {
        input.addEventListener('change', this.onChange.bind(this));
      }, this);

      this.hiddenIdInput = this.querySelector('[data-luxury-variant-id]');
      this.gallery = this.section ? this.section.querySelector('luxury-product-gallery') : null;
      this.initialVariantId = this.hiddenIdInput ? this.hiddenIdInput.value : null;
    }

    getSelectedOptions() {
      var groups = {};
      this.inputs.forEach(function (input) {
        if (input.checked) {
          var position = input.closest('[data-option-position]').getAttribute('data-option-position');
          groups[position] = input.value;
        }
      });
      return groups;
    }

    findMatchingVariant(selected) {
      return this.variants.find(function (variant) {
        var options = [variant.option1, variant.option2, variant.option3];
        return Object.keys(selected).every(function (position) {
          return options[Number(position) - 1] === selected[position];
        });
      });
    }

    onChange() {
      var selected = this.getSelectedOptions();
      var matchedVariant = this.findMatchingVariant(selected);

      this.updateLabels(selected);
      this.updateAvailabilityStates(selected);

      if (matchedVariant) {
        this.updateForVariant(matchedVariant);
      } else {
        this.updateForNoMatch();
      }

      this.dispatchEvent(new CustomEvent('luxury:variant-change', {
        bubbles: true,
        detail: { variant: matchedVariant || null }
      }));
    }

    updateLabels(selected) {
      this.querySelectorAll('[data-option-position]').forEach(function (fieldset) {
        var position = fieldset.getAttribute('data-option-position');
        var valueEl = fieldset.querySelector('[data-luxury-selected-value]');
        if (valueEl && selected[position]) valueEl.textContent = selected[position];

        fieldset.querySelectorAll('[data-luxury-option-value]').forEach(function (label) {
          var input = label.querySelector('.luxury-variants__radio');
          label.classList.toggle('is-selected', !!(input && input.checked));
        });
      });
    }

    /* Recomputes is-soldout state for every option value given the current
       partial selection, so unreachable combinations are visibly disabled. */
    updateAvailabilityStates(selected) {
      var variants = this.variants;
      this.querySelectorAll('[data-luxury-option-value]').forEach(function (label) {
        var position = label.getAttribute('data-option-position');
        var value = label.getAttribute('data-value');
        var testSelection = Object.assign({}, selected);
        testSelection[position] = value;

        var isAvailable = variants.some(function (variant) {
          var options = [variant.option1, variant.option2, variant.option3];
          var matches = Object.keys(testSelection).every(function (pos) {
            return options[Number(pos) - 1] === testSelection[pos];
          });
          return matches && variant.available;
        });

        label.classList.toggle('is-soldout', !isAvailable);
        var input = label.querySelector('.luxury-variants__radio');
        if (input) input.toggleAttribute('data-unavailable', !isAvailable);
      });
    }

    updateForVariant(variant) {
      if (this.hiddenIdInput) this.hiddenIdInput.value = variant.id;

      var form = this.section ? this.section.querySelector('[data-luxury-buy-buttons] form') : null;
      var formIdInput = form ? form.querySelector('[data-luxury-form-variant-id]') : null;
      if (formIdInput) formIdInput.value = variant.id;

      var priceEl = this.section.querySelector('[data-luxury-price]');
      var compareEl = this.section.querySelector('[data-luxury-compare-price]');
      var saleBadge = this.section.querySelector('[data-luxury-sale-badge]');
      var skuRow = this.section.querySelector('[data-luxury-sku-row]');
      var skuEl = this.section.querySelector('[data-luxury-sku]');
      var availabilityEl = this.section.querySelector('[data-luxury-availability]');

      if (priceEl && window.Shopify && window.Shopify.formatMoney) {
      var moneyFormat =
        window.money_format ||
        (window.Shopify && window.Shopify.money_format) ||
        '{{amount}}';

      priceEl.textContent = window.Shopify.formatMoney(
        variant.price,
        moneyFormat
      );
      } else if (priceEl) {
        priceEl.textContent = this.formatMoneyFallback(variant.price);
      }

      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (priceEl) priceEl.classList.toggle('luxury-product-info__price--sale', !!onSale);
      if (compareEl) {
        if (onSale) {
          compareEl.hidden = false;

          if (window.Shopify && window.Shopify.formatMoney) {
            var compareMoneyFormat =
              window.money_format ||
              (window.Shopify && window.Shopify.money_format) ||
              '{{amount}}';

            compareEl.textContent = window.Shopify.formatMoney(
              variant.compare_at_price,
              compareMoneyFormat
            );
          } else {
            compareEl.textContent = this.formatMoneyFallback(variant.compare_at_price);
          }
        } else {
          compareEl.hidden = true;
        }
      }

      if (saleBadge) saleBadge.hidden = !onSale;

      if (skuRow) skuRow.hidden = !variant.sku;
      if (skuEl) skuEl.textContent = variant.sku || '';

      if (availabilityEl) {
        availabilityEl.innerHTML = this.renderAvailabilityBadge(variant);
      }

      this.updateBuyButtonState(variant);
      this.updateMobileBarState(variant);
      this.updateQuantityRule(variant);
      this.updatePickupAvailability(variant);

      if (variant.featured_media && this.gallery && typeof this.gallery.setActiveByMediaId === 'function') {
        this.gallery.setActiveByMediaId(variant.featured_media.id);
      }

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    updateForNoMatch() {
      var button = this.section.querySelector('[data-luxury-add-to-cart]');
      if (button) {
        button.disabled = true;
        var textEl = button.querySelector('[data-luxury-btn-text]');
        if (textEl) textEl.textContent = this.strings.unavailable || 'Unavailable';
      }
    }

    updateBuyButtonState(variant) {
      var button = this.section.querySelector('[data-luxury-add-to-cart]');
      if (!button) return;
      button.disabled = !variant.available;
      var textEl = button.querySelector('[data-luxury-btn-text]');
      if (textEl) {
        textEl.textContent = variant.available
          ? (this.strings.addToCart || 'Add to Cart')
          : (this.strings.soldOut || 'Sold Out');
      }
    }

    updateMobileBarState(variant) {
      var bar = this.section.querySelector('[data-luxury-mobile-sticky]');
      if (!bar) return;

      var priceEl = bar.querySelector('[data-luxury-mobile-price]');
      var ctaEl = bar.querySelector('[data-luxury-mobile-add-to-cart]');

      if (priceEl) {
        if (window.Shopify && window.Shopify.formatMoney) {
          var mobileMoneyFormat =
            window.money_format ||
            (window.Shopify && window.Shopify.money_format) ||
            '{{amount}}';

          priceEl.textContent = window.Shopify.formatMoney(
            variant.price,
            mobileMoneyFormat
          );
        } else {
          priceEl.textContent = this.formatMoneyFallback(variant.price);
        }
      }

      if (ctaEl) {
        ctaEl.disabled = !variant.available;
        ctaEl.textContent = variant.available
          ? (this.strings.addToCart || 'Add to Cart')
          : (this.strings.soldOut || 'Sold Out');
      }
    }

    /* Re-applies the newly selected variant's quantity_rule (min/max/increment)
       to the shared quantity input, clamping the current value into range. */
    updateQuantityRule(variant) {
      var input = this.section ? this.section.querySelector('[data-luxury-qty-input]') : null;
      if (!input) return;
      var rule = variant.quantity_rule || { min: 1, max: null, increment: 1 };
      var min = rule.min || 1;
      var step = rule.increment || 1;

      input.min = min;
      input.step = step;
      if (rule.max) {
        input.max = rule.max;
      } else {
        input.removeAttribute('max');
      }

      var current = parseInt(input.value, 10) || min;
      if (current < min) current = min;
      if (rule.max && current > rule.max) current = rule.max;
      input.value = current;

      var note = this.section.querySelector('[data-luxury-qty-rule-note]');
      if (note) {
        if (step > 1) {
          note.textContent = (this.strings.quantityRuleIncrement || 'Sold in multiples of __INCREMENT__').replace('__INCREMENT__', step);
          note.hidden = false;
        } else if (min > 1) {
          note.textContent = (this.strings.quantityRuleMin || 'Minimum order quantity: __MIN__').replace('__MIN__', min);
          note.hidden = false;
        } else {
          note.hidden = true;
        }
      }
    }

    /* Pickup availability is server-rendered for the page's initial variant
       only (see luxury-pickup-availability.liquid) — it doesn't carry
       accurate data for a different variant selected client-side, so hide
       it rather than show stale store/stock info. */
    updatePickupAvailability(variant) {
      var pickup = this.section ? this.section.querySelector('[data-luxury-pickup-availability]') : null;
      if (!pickup) return;
      pickup.hidden = String(variant.id) !== String(this.initialVariantId);
    }

    renderAvailabilityBadge(variant) {
      if (!variant.available) {
        return '<span class="luxury-badge luxury-badge--sold-out">' + (this.strings.soldOut || 'Sold Out') + '</span>';
      }
      if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 10) {
        var text = (this.strings.lowStockHtml || 'Only {{count}} left in stock').replace('__COUNT__', variant.inventory_quantity).replace('{{ count }}', variant.inventory_quantity).replace('{{count}}', variant.inventory_quantity);
        return '<span class="luxury-badge luxury-badge--low-stock">' + text + '</span>';
      }
      return '<span class="luxury-badge luxury-badge--in-stock">' + (this.strings.inStock || 'In Stock') + '</span>';
    }

    formatMoneyFallback(cents) {
      return (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Quantity Selector                                                   */
  /* ------------------------------------------------------------------ */
  class LuxuryQuantitySelector extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-luxury-qty-input]');
      this.minusBtn = this.querySelector('[data-luxury-qty-minus]');
      this.plusBtn = this.querySelector('[data-luxury-qty-plus]');

      if (this.minusBtn) this.minusBtn.addEventListener('click', this.decrease.bind(this));
      if (this.plusBtn) this.plusBtn.addEventListener('click', this.increase.bind(this));
      if (this.input) this.input.addEventListener('change', this.clamp.bind(this));
    }

    get min() { return parseInt(this.input.min, 10) || 1; }
    get step() { return parseInt(this.input.step, 10) || 1; }

    increase() {
      this.input.value = (parseInt(this.input.value, 10) || this.min) + this.step;
      this.dispatchChange();
    }

    decrease() {
      var next = (parseInt(this.input.value, 10) || this.min) - this.step;
      this.input.value = Math.max(this.min, next);
      this.dispatchChange();
    }

    clamp() {
      var value = parseInt(this.input.value, 10);
      if (isNaN(value) || value < this.min) this.input.value = this.min;
      this.dispatchChange();
    }

    dispatchChange() {
      this.dispatchEvent(new CustomEvent('luxury:quantity-change', {
        bubbles: true,
        detail: { value: parseInt(this.input.value, 10) }
      }));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Buy Buttons (Cart AJAX API)                                         */
  /* ------------------------------------------------------------------ */
  class LuxuryBuyButtons extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-luxury-add-to-cart]');
      this.errorEl = this.querySelector('[data-luxury-buy-error]');
      this.strings = getSectionJSON(this, 'data-luxury-strings') || {};
      this.section = this.closest('section');

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      var mobileButton = this.section ? this.section.querySelector('[data-luxury-mobile-add-to-cart]') : null;
      if (mobileButton) {
        mobileButton.addEventListener('click', function () {
          if (this.form) this.onSubmit(new Event('submit', { cancelable: true }));
        }.bind(this));
      }

      this.initSellingPlans();
    }

    /* One-time vs subscription purchase-option radios: toggles the plan
       group's <select> visibility and keeps the hidden "selling_plan"
       form field (submitted natively by both Add to Cart and Dynamic
       Checkout) in sync. */
    initSellingPlans() {
      this.purchaseOptionInputs = Array.from(this.querySelectorAll('[data-luxury-purchase-option]'));
      this.sellingPlanInput = this.querySelector('[data-luxury-selling-plan-input]');
      if (!this.purchaseOptionInputs.length || !this.sellingPlanInput) return;

      this.purchaseOptionInputs.forEach(function (input) {
        input.addEventListener('change', this.onPurchaseOptionChange.bind(this));
      }, this);

      this.querySelectorAll('[data-luxury-selling-plan-select]').forEach(function (select) {
        select.addEventListener('change', this.onSellingPlanSelectChange.bind(this));
      }, this);
    }

    onPurchaseOptionChange(event) {
      var value = event.target.value;
      var isOneTime = value === 'one_time';

      this.querySelectorAll('.luxury-selling-plans__option').forEach(function (label) {
        var input = label.querySelector('[data-luxury-purchase-option]');
        label.classList.toggle('is-selected', !!(input && input.checked));
      });

      this.querySelectorAll('[data-luxury-selling-plan-group]').forEach(function (group) {
        group.hidden = group.getAttribute('data-luxury-selling-plan-group') !== value;
      });

      if (isOneTime) {
        this.sellingPlanInput.value = '';
      } else {
        var activeGroup = this.querySelector('[data-luxury-selling-plan-group="' + value + '"]');
        var select = activeGroup ? activeGroup.querySelector('[data-luxury-selling-plan-select]') : null;
        this.sellingPlanInput.value = select ? select.value : '';
      }
    }

    onSellingPlanSelectChange(event) {
      if (this.sellingPlanInput) this.sellingPlanInput.value = event.target.value;
    }

    onSubmit(event) {
      event.preventDefault();
      if (!this.form || this.submitButton.disabled) return;

      var formData = new FormData(this.form);
      var quantityInput = this.section ? this.section.querySelector('[data-luxury-qty-input]') : null;
      if (quantityInput) formData.set('quantity', quantityInput.value || '1');

            this.setState('loading');

      var cartAddUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_add_url) || '/cart/add.js';

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (response) { return response.json().then(function (data) { return { ok: response.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            this.showError(result.data.description || this.strings.genericError || 'Something went wrong.');
            this.setState('idle');
            return;
          }
          this.setState('success');
          this.dispatchEvent(new CustomEvent('luxury:cart-updated', { bubbles: true, detail: result.data }));
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: result.data }));
          this.refreshNativeCart();
          setTimeout(function () { this.setState('idle'); }.bind(this), 1800);
        }.bind(this))
        .catch(function () {
          this.showError(this.strings.genericError || 'Something went wrong. Please try again.');
          this.setState('idle');
        }.bind(this));
    }

    refreshNativeCart() {
      if (!window.Shopify || typeof window.Shopify.getCart !== 'function') return;
      if (!window.scoder || typeof window.scoder.updateSidebarCart !== 'function') return;

      window.Shopify.getCart(function (cart) {
        if (window.after_add_to_cart && window.after_add_to_cart.type === 'cart') {
          window.location.href = (window.Shopify.routes && window.Shopify.routes.cart_url) || '/cart';
          return;
        }
        document.body.classList.add('cart-sidebar-show');
        window.scoder.updateSidebarCart(cart);
      });
    }

    setState(state) {
      if (!this.submitButton) return;
      if (state === 'idle') {
        this.submitButton.removeAttribute('data-state');
      } else {
        this.submitButton.setAttribute('data-state', state);
      }
      this.submitButton.disabled = state === 'loading';
      if (this.errorEl) this.errorEl.hidden = true;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Accordion (one panel open at a time)                                */
  /* ------------------------------------------------------------------ */
  class LuxuryAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-luxury-accordion-item]'));
      this.items.forEach(function (item) {
        var summary = item.querySelector('[data-luxury-accordion-trigger]');
        if (!summary) return;
        summary.addEventListener('click', function (event) {
          event.preventDefault();
          var willOpen = !item.open;
          this.items.forEach(function (other) {
            if (other !== item) other.removeAttribute('open');
          });
          item.toggleAttribute('open', willOpen);
        }.bind(this));
      }, this);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sticky Panel                                                        */
  /* ------------------------------------------------------------------ */
  class LuxuryStickyPanel extends HTMLElement {
    connectedCallback() {
      if (this.getAttribute('data-sticky-enabled') !== 'true') return;
      this.offset = parseInt(this.getAttribute('data-sticky-offset'), 10) || 0;
      this.updateOffset();
      window.addEventListener('resize', this.updateOffset.bind(this));
    }

    updateOffset() {
      var header = document.querySelector('sticky-header, .shopify-section-header, header');
      var headerHeight = header ? header.getBoundingClientRect().height : 0;
      this.style.setProperty('--sticky-top', (headerHeight + this.offset) + 'px');
    }
  }

  customElements.define('luxury-product-gallery', LuxuryProductGallery);
  customElements.define('luxury-variant-picker', LuxuryVariantPicker);
  customElements.define('luxury-quantity-selector', LuxuryQuantitySelector);
  customElements.define('luxury-buy-buttons', LuxuryBuyButtons);
  customElements.define('luxury-accordion', LuxuryAccordion);
  customElements.define('luxury-sticky-panel', LuxuryStickyPanel);

  /* ------------------------------------------------------------------ */
  /* Size guide modal (native <dialog>)                                  */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-luxury-size-guide-open]');
    if (opener) {
      var wrapper = opener.parentElement;
      var modal = wrapper ? wrapper.querySelector('[data-luxury-size-guide-modal]') : document.querySelector('[data-luxury-size-guide-modal]');
      if (modal && typeof modal.showModal === 'function') modal.showModal();
    }
    var closer = event.target.closest('[data-luxury-size-guide-close]');
    if (closer) {
      var dialog = closer.closest('dialog');
      if (dialog) dialog.close();
    }
  });

  document.addEventListener('click', function (event) {
    var dialog = event.target.closest('dialog[data-luxury-size-guide-modal]');
    if (dialog && event.target === dialog) dialog.close();
  });

  /* ------------------------------------------------------------------ */
  /* Mobile sticky add-to-cart bar visibility                            */
  /* ------------------------------------------------------------------ */
  function initMobileStickyBar() {
    document.querySelectorAll('[data-luxury-mobile-sticky]').forEach(function (bar) {
      var section = bar.closest('section');
      var purchaseBlock = section ? section.querySelector('.luxury-product__purchase') : null;
      if (!purchaseBlock || typeof IntersectionObserver === 'undefined') return;

      bar.hidden = false;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && window.innerWidth < 750);
        });
      }, { threshold: 0 });
      observer.observe(purchaseBlock);

      window.addEventListener('resize', function () {
        if (window.innerWidth >= 750) bar.classList.remove('is-visible');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /* ------------------------------------------------------------------ */
  function initScrollReveal() {
    var targets = document.querySelectorAll('[data-scroll-reveal]');
    if (!targets.length) return;
    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    targets.forEach(function (el) { observer.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initMobileStickyBar();
      initScrollReveal();
    });
  } else {
    initMobileStickyBar();
    initScrollReveal();
  }
})();
